import { motion } from "framer-motion";
import { Layers, PenTool, Signpost } from "lucide-react";
import { mediaUrl } from "@/lib/api";
import ShopButton from "@/components/mall/ShopButton";
import { BillboardFrame, Bullets, Lines, MoreDetails, Panel, RoomNumber } from "@/components/mall/BillboardKit";

const SKY = "#38bdf8";
const SIGNS_IMG = "/api/files/43f93f36-0a06-4cc6-afed-249afc6d9b44";

const MAKES = [
    "Business cards, posters and postcards",
    "Plastic yard signs",
    "Metal wall signs",
    "Vinyl lettering for cars and trucks",
    "Vinyl and mesh banners",
];

const MATERIALS = [
    "3M adhesive vinyl",
    "Removable floor graphics",
    "HD vinyl banner",
    "HDPE banner",
    "Mesh banner",
    "Poster paper",
    "Coroplast, 4 mil",
];

const SQFT = [
    ["Banners start at", "$2 a square foot"],
    ["Other materials cost", "$5, $10 or $15 a square foot"],
];

const DECALS = [
    ["A straight cut", "$0.03 a square inch"],
    ["A shaped cut", "$0.07 a square inch"],
    ["A custom shape", "$0.10 a square inch"],
];

/** Room 03 — signs. Designed online, priced by the square foot. */
export default function SignsRoom() {
    return (
        <BillboardFrame testid="frame-signs" room="signs" note="signs & banners">
        <section className="dark-block relative overflow-hidden" data-testid="mall-room-signs">
            <img src={mediaUrl(SIGNS_IMG)} alt="" className="absolute inset-0 h-full w-full object-cover opacity-20" />
            <div className="absolute inset-0" style={{ background: "linear-gradient(105deg, #09090b 8%, rgba(9,9,11,0.9) 55%, rgba(56,189,248,0.14) 100%)" }} />
            <div className="pointer-events-none absolute -right-20 bottom-0 h-72 w-72 rounded-full blur-[130px]" style={{ background: "rgba(56,189,248,0.25)" }} />

            <div className="relative mx-auto max-w-7xl px-4 py-10 sm:px-6 md:py-12">
                <div className="grid gap-10 lg:grid-cols-12 lg:items-center">
                    <div className="lg:col-span-7">
                        <RoomNumber n="03" room="Signs" accent={SKY} dark className="mt-6" />
                        <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                            data-testid="signs-headline"
                            className="mt-4 font-display text-4xl font-black leading-[0.94] tracking-tighter text-white sm:text-5xl lg:text-6xl">
                            YOU DESIGN YOUR SIGN ONLINE.<br /><span style={{ color: SKY }}>WE PRINT IT FROM $2 A SQUARE FOOT.</span>
                        </motion.h2>
                        <p className="mt-5 max-w-xl text-base text-zinc-300 md:text-lg" data-testid="signs-body">
                            Pick a material, type in your size and drop your art on the sign. The price changes as
                            you go. Grommets, pole pockets, hems, stakes and shaped cuts are all in the builder. When
                            it looks right, you pay and we print it.
                        </p>
                        <div className="mt-8 flex flex-wrap gap-3">
                            <ShopButton room="signs" tone="paper" testid="room-signs-cta"
                                title="Which shop makes your sign?" path={(s) => `/signs?store=${s}`}>
                                <Signpost size={17} /> Design a sign
                            </ShopButton>
                            <ShopButton room="signs" tone="outline" testid="room-signs-banner"
                                title="Which shop makes your sign?" path={(s) => `/signs/banner?store=${s}`}>
                                <Layers size={16} /> Start with a banner
                            </ShopButton>
                        </div>
                    </div>

                    <div className="grid gap-5 lg:col-span-5">
                        <Panel icon={PenTool} title="What we make" accent={SKY} dark testid="signs-makes">
                            <Bullets items={MAKES} accent={SKY} dark />
                        </Panel>
                    </div>
                </div>

                <MoreDetails dark testid="signs-details" className="mt-9" label="Materials, prices by the square foot and decal prices">
                <div className="grid gap-5 md:grid-cols-3">
                    <Panel title="Materials we print on" accent={SKY} dark testid="signs-materials">
                        <Bullets items={MATERIALS} accent={SKY} dark />
                    </Panel>
                    <Panel title="Signs are priced by the square foot" accent={SKY} dark testid="signs-sqft">
                        <Lines rows={SQFT} accent="#ffffff" dark />
                        <p className="mt-3 text-xs text-zinc-400">
                            The material you pick sets the price per square foot. The price updates every time you change the size.
                        </p>
                    </Panel>
                    <Panel title="Decals are priced by the square inch" accent={SKY} dark testid="signs-decals">
                        <Lines rows={DECALS} accent="#ffffff" dark />
                        <p className="mt-3 text-xs text-zinc-400">A custom shape costs more because it takes longer to cut.</p>
                    </Panel>
                </div>
                </MoreDetails>
            </div>
        </section>
        </BillboardFrame>
    );
}
